import { useRoute } from 'vue-router';

import Organization from '@/common/models/memberships/Organization';
import JourneyAnalysisOld from '@/org-app/store/modules/journeyAnalysisOld';

import actions from './actions';
import {
  Accounts,
  AdvancedReportDefinitions,
  AdvancedReportExecutions,
  AdvancedReportSchedules,
  Advertisers,
  Announcements,
  AudienceFiles,
  AudienceLogics,
  AudienceRuleCustomGroup,
  AudienceSegments,
  AvailsImport,
  Campaigns,
  Creatives,
  CuratedPackages,
  CustomEvents,
  Demographics,
  EntityFees,
  EpsilonSegments,
  ExternalSegments,
  GeofenceGroups,
  HeaderBidderPresets,
  HeaderBidderSettings,
  JourneyAnalysis,
  LiftDashboards,
  LineItems,
  Measurements,
  MediaOwners,
  MobileLineItems,
  Networks,
  OrganizationCustomFields,
  OwnerYieldManagerSsps,
  PackageGroup,
  Packages,
  Plans,
  PostalCodeFiles,
  PreSaleHeatmaps,
  ProductFeatures,
  ReferenceData,
  ReportDefinitions,
  ReportExecutions,
  ReportSchedules,
  RevenueSplits,
  Sites,
  UnitPacks,
  Units,
  Users,
  UserSettings,
} from './modules/index';
import mutations from './mutations';
import initialState from './state';

type OrgAppStore = {
  state: typeof initialState;
  mutations: typeof mutations;
  actions: typeof actions;
  modules: {
    Advertisers: typeof Advertisers;
    Accounts: typeof Accounts;
    AdvancedReportDefinitions: typeof AdvancedReportDefinitions;
    AdvancedReportExecutions: typeof AdvancedReportExecutions;
    AdvancedReportSchedules: typeof AdvancedReportSchedules;
    Announcements: typeof Announcements;
    AudienceFiles: typeof AudienceFiles;
    AudienceLogics: typeof AudienceLogics;
    AudienceSegments: typeof AudienceSegments;
    AudienceRuleCustomGroup: typeof AudienceRuleCustomGroup;
    AvailsImport: typeof AvailsImport;
    Campaigns: typeof Campaigns;
    Creatives: typeof Creatives;
    CuratedPackages: typeof CuratedPackages;
    Demographics: typeof Demographics;
    EpsilonSegments: typeof EpsilonSegments;
    CustomEvents: typeof CustomEvents;
    GeofenceGroups: typeof GeofenceGroups;
    HeaderBidderPresets: typeof HeaderBidderPresets;
    HeaderBidderSettings: typeof HeaderBidderSettings;
    JourneyAnalysis: typeof JourneyAnalysis;
    JourneyAnalysisOld: typeof JourneyAnalysisOld;
    LiftDashboards: typeof LiftDashboards;
    LineItems: typeof LineItems;
    MediaOwners: typeof MediaOwners;
    MobileLineItems: typeof MobileLineItems;
    Networks: typeof Networks;
    OrganizationCustomFields: typeof OrganizationCustomFields;
    OwnerYieldManagerSsps: typeof OwnerYieldManagerSsps;
    Packages: typeof Packages;
    PackageGroup: typeof PackageGroup;
    Plans: typeof Plans;
    PostalCodeFiles: typeof PostalCodeFiles;
    PreSaleHeatmaps: typeof PreSaleHeatmaps;
    ProductFeatures: typeof ProductFeatures;
    ReferenceData: typeof ReferenceData;
    ReportDefinitions: typeof ReportDefinitions;
    ReportExecutions: typeof ReportExecutions;
    ReportSchedules: typeof ReportSchedules;
    RevenueSplits: typeof RevenueSplits;
    Sites: typeof Sites;
    Units: typeof Units;
    UnitPacks: typeof UnitPacks;
    Users: typeof Users;
    UserSettings: typeof UserSettings;
    EntityFees: typeof EntityFees;
    Measurements: typeof Measurements;
    ExternalSegments: typeof ExternalSegments;
  };
  getters: {
    isDemandOrganization: (state) => boolean;
    isSupplyOrganization: (state) => boolean;
    isCuratorOrganization: (state) => boolean;
    isStroeerOrganization: (state) => boolean;
    isOrgOnInternalExchange: (state) => boolean;
    isSidekickOrganization: (state) => boolean;
    isParentOrganization: (state) => boolean;
    currentOrganization: (state) => Organization;
    defaultAdServerName: (state) => string;
    adServerLogoUrl: (state) => string;
    enableAudienceGeoStatistics: (state) => boolean;
    isTTDOrganization: (state) => boolean;
  };
};

const store: OrgAppStore = {
  namespaced: true,
  strict: false,
  state: initialState,
  mutations,
  actions,
  modules: {
    Advertisers,
    Accounts,
    AdvancedReportDefinitions,
    AdvancedReportExecutions,
    AdvancedReportSchedules,
    Announcements,
    AudienceFiles,
    AudienceLogics,
    AudienceSegments,
    AudienceRuleCustomGroup: AudienceRuleCustomGroup,
    AvailsImport,
    Campaigns,
    Creatives,
    CuratedPackages,
    Demographics,
    EpsilonSegments,
    CustomEvents,
    GeofenceGroups,
    HeaderBidderPresets,
    HeaderBidderSettings,
    JourneyAnalysis,
    JourneyAnalysisOld,
    LiftDashboards,
    LineItems,
    MediaOwners,
    MobileLineItems,
    Networks,
    OrganizationCustomFields,
    OwnerYieldManagerSsps,
    Packages,
    PackageGroup,
    Plans,
    PostalCodeFiles,
    PreSaleHeatmaps,
    ProductFeatures,
    ReferenceData,
    ReportDefinitions,
    ReportExecutions,
    ReportSchedules,
    RevenueSplits,
    Sites,
    Units,
    UnitPacks,
    Users,
    UserSettings,
    EntityFees,
    Measurements,
    ExternalSegments,
  },
  getters: {
    isDemandOrganization: (state) =>
      state.organization !== null && state.organization !== undefined ? state.organization.isDemandOrganization : false,
    isSupplyOrganization: (state) =>
      state.organization !== null && state.organization !== undefined ? state.organization.isSupplyOrganization : false,
    isParentOrganization: (state) =>
      state.organization !== null && state.organization !== undefined ? state.organization.isParentOrganization : false,
    isCuratorOrganization: (state) =>
      state.organization !== null && state.organization !== undefined
        ? state.organization.isCuratorOrganization
        : false,
    isStroeerOrganization: (state) => {
      if (state.organization !== null && state.organization !== undefined) {
        if (state.organization.isStroeerOrganization) {
          return state.organization.isStroeerOrganization;
        } else {
          // Public
          const route = useRoute();
          if (route.path.includes('/proposal/') || route.path.includes('/monitoring/')) {
            return Organization.fromObject(state.organization).isStroeerOrganization;
          }
        }
      }
      return false;
    },
    isOrgOnInternalExchange: (state) =>
      state.organization !== null && state.organization !== undefined ? state.organization.exchangeID === 1 : false,
    isSidekickOrganization: (state) => {
      return state?.organization?.isSidekick;
    },
    isTTDOrganization: (state) => {
      return state?.organization?.isTTD;
    },
    currentOrganization: (state) =>
      state.organization !== null && state.organization !== undefined ? state.organization : null,
    defaultAdServerName: (state) => (state.organizationSettings ? state.organizationSettings.defaultAdServerName : ''),
    adServerLogoUrl: (state) => (state.organizationSettings ? state.organizationSettings.adServerLogoUrl : ''),
    enableAudienceGeoStatistics: (state) =>
      state.organizationSettings ? !!state.organizationSettings.audienceGeoStatisticsUrl : false,
    userSettings: (state) => state.UserSettings?.userSettings || [],
  },
} as OrgAppStore;

export default store;
